import React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { Badge } from "@rentablo/components";
import { ExtendedAccount } from "@features/depots/types";
import { getUpdateBankConnectionUrl, MandatorConditional } from "@helpers";
import { useHasBlockExtender } from "@hooks";
import { useCurrencyDisplay } from "@providers";
import { AccountPreviewChart } from "../../Chart";
import { AccountIconAndName } from "../AccountPreviewCard/AccountIconAndName";
import {
  CardExtender,
  cardExtenderConfig,
} from "../AccountPreviewCard/CardExtender";
import { AccountRowSummary } from "../AccountPreviewCard/AccountRowSummary";
import { AccountPerformance } from "./hooks/useAccountsPerformance";
import { KpmgReportingCheckbox } from "./KpmgReportingCheckbox";

const AccountPreviewCardContainer = styled.div<{
  showNotification: boolean;
  clickable: boolean;
  isLast: boolean;
}>(({ theme, clickable, isLast }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  alignItems: "center",
  rowGap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  minHeight: "100px",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.border.secondary}`,
  cursor: clickable ? "pointer" : "default",
  [theme.breakpoints.down("sm")]: {
    minHeight: "80px",
    rowGap: theme.spacing(2),
  },
  ...(isLast && {
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
  }),
}));

const ChartContainer = styled(Box)({
  height: 48,
});

interface Props {
  account: ExtendedAccount;
  onClick: () => void;
  performance?: AccountPerformance;
  isLast?: boolean;
  isPrimaryReference?: boolean;
}

export const AccountView = ({
  account,
  onClick,
  performance,
  isLast,
  isPrimaryReference = false,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const chipsWidth = isLg ? "165px" : "230px";
  const isRefAccount = Boolean((account.depotIds?.length ?? 0) > 0);
  const showExtender = useHasBlockExtender(cardExtenderConfig);
  const { globalCurrency } = useCurrencyDisplay();
  const {
    id,
    name,
    bankName,
    type,
    bankConnectionId,
    accountNumber,
    iconUrl = "",
    syncRequired,
    bankConnectionType,
    depotIds,
    currency,
  } = account;

  const showNativeAnnotation = Boolean(currency && currency !== globalCurrency);

  const isDepot = type === "01_depot";

  const {
    portfolioValue = 0,
    chartData = [],
    sinceYear,
    changeAbsCashFlowAdjusted,
    nativePortfolioValue,
    isFetching = false,
  } = performance ?? {};

  const updateConnectionUrl = getUpdateBankConnectionUrl(account);
  const handleClickUpdate = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (syncRequired && updateConnectionUrl) navigate(updateConnectionUrl);
  };

  return (
    <AccountPreviewCardContainer
      data-testid="account-row"
      showNotification={syncRequired}
      clickable={Boolean(onClick)}
      onClick={onClick ? () => onClick() : undefined}
      isLast={isLast ?? false}
    >
      <MandatorConditional showFor={["kpmg-germany"]}>
        <KpmgReportingCheckbox account={account} />
      </MandatorConditional>
      <Box minWidth={0}>
        <AccountIconAndName
          isReference={(depotIds?.length ?? 0) > 0}
          bankLogoURL={iconUrl}
          accountType={type}
          accountName={name}
          bankName={bankName}
          bankCode={accountNumber}
          nativeCurrencyAnnotation={showNativeAnnotation ? currency : undefined}
          badge={
            isPrimaryReference ? (
              <Badge variant="primary" sx={{ flexShrink: 0 }}>
                {t("accounts.primaryReferenceBadge")}
              </Badge>
            ) : undefined
          }
        />
      </Box>
      {!isMd && (
        <Box px={4} minWidth={0}>
          <ChartContainer>
            <AccountPreviewChart
              chartData={chartData}
              isFetching={isFetching}
            />
          </ChartContainer>
        </Box>
      )}
      {showExtender && !isMd && (
        <Box minWidth={chipsWidth}>
          {!isRefAccount && <CardExtender account={account} />}
        </Box>
      )}
      <Box>
        <AccountRowSummary
          amount={portfolioValue}
          currency={globalCurrency}
          sinceYear={isDepot ? sinceYear : undefined}
          changeAbs={isDepot ? (changeAbsCashFlowAdjusted ?? 0) : 0}
          showNotification={!isRefAccount && syncRequired}
          onClickUpdate={
            isRefAccount || !updateConnectionUrl ? undefined : handleClickUpdate
          }
          nativePortfolioValue={
            showNativeAnnotation ? nativePortfolioValue : undefined
          }
          nativeCurrency={showNativeAnnotation ? currency : undefined}
        />
      </Box>
      {showExtender && !isRefAccount && isMd && (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <CardExtender account={account} />
        </Box>
      )}
    </AccountPreviewCardContainer>
  );
};
